import GroupLayer from '@planet/maps/layer/Group';
import TileLayer from '@planet/maps/layer/Tile';
import XYZSource from '@planet/maps/source/XYZ';
import GeoJSON from 'ol/format/GeoJSON';
import {getVectorContext} from 'ol/render.js';
import {array, bool, func, object, string} from 'prop-types';
import React, {useMemo} from 'react';

import CompareRasters from './compare-rasters.jsx';
import {COMPARE_METHODS} from '@explorer/constants';
import {CLIP_STYLE} from '@explorer/util/map-edit-styles';

const CompareTileLayers = ({
  compareTileConfigs = [],
  clipGeometry = null,
  visible,
  onTileLoad,
  values,
  compareMethod,
}) => {
  // useMemo is used instead of useCallback
  //  so that geometry can be used as a closure in the function
  //  that is returned. The object is returned for some
  //  future-proofing in case we need a onPrerender or
  //  the geometry to be broken out.
  const {onPrerender, onPostrender} = useMemo(() => {
    const geometry = clipGeometry
      ? new GeoJSON().readGeometry(clipGeometry)
      : null;

    return {
      onPrerender: index => event => {
        const {context} = event;
        context.save();
        if (geometry) {
          const vectorContext = getVectorContext(event);
          vectorContext.setStyle(CLIP_STYLE);
          vectorContext.drawGeometry(geometry);
          context.clip();
        }

        if (compareMethod === COMPARE_METHODS.SLIDER) {
          const width = context.canvas.width * values[COMPARE_METHODS.SLIDER];
          const start = index === 1 ? width : 0;
          const end = index === 1 ? context.canvas.width - width : width;

          context.beginPath();
          context.rect(start, 0, end, context.canvas.height);
          context.clip();
        }
      },
      onPostrender: () => event => {
        event.context.restore();
      },
    };
  }, [clipGeometry, compareMethod, values]);

  const sourceOptions = useMemo(() => {
    const options = {
      crossOrigin: 'anonymous',
    };

    // this prevents needing to use the fetch loader
    //   when `onTileLoad` is undefined.
    if (onTileLoad) {
      options.tileLoadFunction = (imageTile, src) => {
        return fetch(src)
          .then(res => {
            onTileLoad(res);
            return res.blob();
          })
          .then(blob => {
            const reader = new FileReader();
            reader.onload = evt => {
              imageTile.getImage().src = evt.target.result;
            };
            reader.readAsDataURL(blob);
          })
          .catch(() => {});
      };
    }

    return options;
  }, [onTileLoad]);

  return (
    <GroupLayer visible={visible}>
      {compareTileConfigs.map((compareGroupConfig, idx) => {
        const opacity =
          idx === 1 && compareMethod === COMPARE_METHODS.FADE
            ? values[COMPARE_METHODS.FADE]
            : 1;

        return (
          // eslint-disable-next-line react/no-array-index-key
          <GroupLayer key={`compareLayer${idx}`} opacity={opacity}>
            {compareGroupConfig.rasterConfigs.map(config => (
              <TileLayer
                key={`${config.id}_${config.maxZoom}`}
                options={{
                  extent: config.extent,
                }}
                onPostrender={onPostrender(idx)}
                onPrerender={onPrerender(idx)}
              >
                <XYZSource
                  url={config.url}
                  options={{
                    maxZoom: Math.ceil(config.maxZoom),
                    ...sourceOptions,
                  }}
                />
              </TileLayer>
            ))}
          </GroupLayer>
        );
      })}
      {compareTileConfigs.length === 2 && (
        <CompareRasters
          visible={compareMethod === COMPARE_METHODS.DIFF}
          onPostrender={onPostrender()}
          onPrerender={onPrerender()}
          urls={compareTileConfigs
            .flatMap(group => group.rasterConfigs)
            .map(config => config.url)}
          rangeValues={values[COMPARE_METHODS.DIFF]}
        />
      )}
    </GroupLayer>
  );
};

CompareTileLayers.propTypes = {
  compareTileConfigs: array,
  clipGeometry: object,
  visible: bool,
  onTileLoad: func,
  compareMethod: string,
  values: object,
};

export default CompareTileLayers;
